import {
  AtLeastOne,
  createViewComponentMapping,
  deleteViewComponentMapping,
  EntityViewComponent,
  EntityViewComponentFieldMapping,
  getViewComponentMappings,
  updateViewComponentMapping,
} from "@/services/entity-view-components.service";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

const KNOWN_COMPONENTS: Array<EntityViewComponent> = [
  {
    name: "Title & Description",
    description: "Display a simple card with a title and a description",
    type: "title-and-description",
    fields: [
      {
        field_name: "title",
        required: true,
      },
      {
        field_name: "description",
        required: true,
      },
    ],
    default_opts: {
      title: "Your title?",
      description: "Your description",
    },
  },
  {
    name: "Card with image",
    description: "Display a card with an image",
    type: "card-with-image",
    fields: [
      {
        field_name: "image",
        required: false,
      },
      {
        field_name: "name",
        required: true,
      },
      {
        field_name: "subname",
        required: false,
      },
      {
        field_name: "description",
        required: false,
      },
    ],
    default_opts: {
      name: "Daniel Lyder",
      subname: "Software Engineer",
      description: "Wiedenbrücker Str. 53, 59555, Lippstadt",
      image: "/images/logos/pylo-signet.png",
    },
  },
];

export const useEntityViewComponents = () => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["entity-view-components"],
    queryFn: getViewComponentMappings,
  });

  const getComponentMapping = useCallback(
    (opts: AtLeastOne<{ entityId: string; entityName: string }>) => {
      return query.data?.find((x) => {
        if (opts.entityId) {
          return x.entity?.data?.id === opts.entityId;
        }
        return x.entity?.data?.name === opts.entityName;
      });
    },
    [query.data],
  );

  const getFieldsToFetch = useCallback(
    (opts: AtLeastOne<{ entityId: string; entityName: string }>) => {
      const mapping = getComponentMapping(opts);
      if (!mapping || !mapping.config) return undefined;
      return Array.from(
        new Set(
          (JSON.parse(String(mapping.config)) as Array<EntityViewComponentFieldMapping>).flatMap(
            (x) => x.entityFields,
          ),
        ),
      );
    },
    [getComponentMapping],
  );

  const invalidateQuery = () => {
    queryClient.invalidateQueries({
      queryKey: ["entity-view-components"],
    });
  };

  const { mutate: createViewComponentMappingMutation } = useMutation({
    mutationFn: createViewComponentMapping,
    onSuccess: () => {
      invalidateQuery();
    },
  });

  const { mutate: updateViewComponentMappingMutation } = useMutation({
    mutationFn: updateViewComponentMapping,
    onSuccess: () => {
      invalidateQuery();
    },
  });

  const { mutate: deleteViewComponentMappingMutation } = useMutation({
    mutationFn: deleteViewComponentMapping,
    onSuccess: () => {
      invalidateQuery();
    },
  });

  return {
    ...query,
    getFieldsToFetch,
    getComponentMapping,
    invalidateQuery,
    components: KNOWN_COMPONENTS,
    deleteViewComponentMappingMutation,
    updateViewComponentMappingMutation,
    createViewComponentMappingMutation,
  };
};
